<template>
  <div class="view-container">
    <div class="search-panel">
      <div class="search-row">
        <div class="search-item">
          <label><i class="fas fa-search"></i> 关键字:</label>
          <input v-model="searchText" type="text" placeholder="Key/模块/描述" class="search-input" @keyup.enter="handleSearch">
        </div>
        <div class="search-actions">
          <button @click="handleSearch" class="btn btn-primary search-btn"><i class="fas fa-search"></i> 查询</button>
          <button @click="resetSearch" class="btn btn-default search-btn"><i class="fas fa-undo"></i> 重置</button>
        </div>
      </div>
    </div>

    <div class="table-wrapper">
      <div class="table-header-row">
        <button class="btn btn-primary btn-sm" @click="handleAdd"><i class="fas fa-plus"></i> 新增</button>
        <button @click="toggleColumnSelector" class="btn btn-default btn-sm column-settings-btn"><i class="fas fa-columns"></i> 列设置</button>
      </div>
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th v-for="column in visibleColumns" :key="column.key"
                :class="{ 'sortable': column.sortable, 'sorted-asc': sortColumn === column.key && sortOrder === 'asc', 'sorted-desc': sortColumn === column.key && sortOrder === 'desc' }"
                @click="handleSort(column.key)"
                :draggable="true"
                @dragstart="dragStart($event, column)"
                @dragover.prevent
                @drop="dropColumn($event, column)"
                class="column-header"
                :style="column.width ? { width: column.width, maxWidth: column.width, minWidth: column.width } : {}">
                <span>{{ column.label }}</span>
                <i v-if="column.sortable" class="fas" :class="sortColumn === column.key ? (sortOrder === 'asc' ? 'fa-chevron-up' : 'fa-chevron-down') : 'fa-sort'"></i>
              </th>
              <th class="action-column">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in paginatedSettings" :key="row.guid">
              <td v-for="column in visibleColumns" :key="column.key"
                :style="column.width ? { width: column.width, maxWidth: column.width, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}">
                <template v-if="column.key === 'useSYSType'">
                  <span class="status-badge" :class="row.useSYSType === 1 ? 'enabled' : 'disabled'">{{ row.useSYSType === 1 ? '是' : '否' }}</span>
                </template>
                <template v-else-if="column.key === 'active'">
                  <span class="status-badge" :class="row.active === 1 ? 'enabled' : 'disabled'">{{ row.active === 1 ? '启用' : '禁用' }}</span>
                </template>
                <template v-else>
                  {{ row[column.key] !== undefined && row[column.key] !== null && row[column.key] !== '' ? row[column.key] : '-' }}
                </template>
              </td>
              <td class="action-column">
                <button @click="handleEdit(row)" class="btn btn-primary btn-xs" title="编辑"><i class="fas fa-edit"></i></button>
                <button @click="handleDelete(row)" class="btn btn-danger btn-xs" title="删除"><i class="fas fa-trash"></i></button>
              </td>
            </tr>
            <tr v-if="paginatedSettings.length === 0"><td :colspan="visibleColumns.length + 1" class="empty-cell">暂无系统参数数据</td></tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-row">
        <div class="pagination-info">共 {{ totalSettings }} 条记录</div>
        <div class="pagination-controls">
          <div class="page-size-selector"><span>每页</span><select v-model="pageSize" class="page-size-input form-control"><option :value="20">20</option><option :value="50">50</option><option :value="75">75</option><option :value="100">100</option></select><span>条</span></div>
          <button @click="prevPage" :disabled="currentPage===1" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-left"></i></button>
          <div class="page-numbers"><button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="['page-btn',{active:currentPage===page,disabled:page==='...'}]" :disabled="page==='...'">{{ page }}</button></div>
          <button @click="nextPage" :disabled="currentPage>=totalPages" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-right"></i></button>
          <div class="page-jump"><span>跳转到</span><input type="number" v-model="jumpPage" min="1" :max="totalPages" class="jump-input" @keyup.enter="handleJump"><span>页</span><button @click="handleJump" class="btn btn-primary btn-sm jump-btn">GO</button></div>
        </div>
      </div>
    </div>

    <!-- 列设置弹窗 -->
    <div v-if="showColumnSelector" class="modal-overlay" @click.self="toggleColumnSelector">
      <div class="modal-container modal-sm">
        <div class="modal-header"><h4>列设置</h4><button @click="toggleColumnSelector" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <label v-for="col in allColumns" :key="col.key" class="column-checkbox">
            <input type="checkbox" v-model="col.visible" @change="updateVisibleColumns">
            <span>{{ col.label }}</span>
          </label>
        </div>
        <div class="modal-footer"><button @click="resetColumns" class="btn btn-default">重置</button><button @click="toggleColumnSelector" class="btn btn-primary">确定</button></div>
      </div>
    </div>

    <!-- 编辑弹窗 -->
    <div v-if="showEditModal" class="modal-overlay">
      <div class="modal-container modal-lg">
        <div class="modal-header"><h3>{{ editingGuid ? '编辑系统参数' : '新增系统参数' }}</h3><button @click="closeEditModal" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group" style="flex:1;position:relative">
              <label>模块 *</label>
              <div class="combo-box">
                <input v-model="editForm.fromModule" type="text" class="form-control" placeholder="输入或选择模块" @focus="onModuleFocus" @input="filterModuleOptions" @blur="onModuleBlur">
                <div v-if="showModuleDropdown && filteredModuleOptions.length > 0" class="combo-dropdown">
                  <div v-for="m in filteredModuleOptions" :key="m" class="combo-option" @mousedown.prevent="selectModule(m)">{{ m }}</div>
                </div>
              </div>
            </div>
            <div class="form-group" style="flex:1;position:relative">
              <label>子模块</label>
              <div class="combo-box">
                <input v-model="editForm.fromSubModule" type="text" class="form-control" placeholder="输入或选择子模块" @focus="onSubModuleFocus" @input="filterSubModuleOptions" @blur="onSubModuleBlur">
                <div v-if="showSubModuleDropdown && filteredSubModuleOptions.length > 0" class="combo-dropdown">
                  <div v-for="s in filteredSubModuleOptions" :key="s" class="combo-option" @mousedown.prevent="selectSubModule(s)">{{ s }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group" style="flex:2"><label>描述</label><input v-model="editForm.description" type="text" class="form-control" placeholder="描述信息"></div>
            <div class="form-group" style="flex:1"><label>排序</label><input v-model.number="editForm.keyOrder" type="number" class="form-control" placeholder="0"></div>
          </div>
          <div class="form-row">
            <div class="form-group" style="flex:1"><label>Key *</label><input v-model="editForm.keys" type="text" class="form-control" placeholder="如: MinLength"></div>
            <div class="form-group" style="flex:1"><label>SubKey</label><input v-model="editForm.subKeys" type="text" class="form-control" placeholder="子键（通常留空）"></div>
          </div>
          <div class="form-row">
            <div class="form-group" style="flex:2"><label>Value</label><input v-model="editForm.sValues" type="text" class="form-control" placeholder="配置值"></div>
            <div class="form-group" style="flex:1"><label>SubValue</label><input v-model="editForm.subValues" type="text" class="form-control" placeholder="辅助值"></div>
          </div>
          <div class="form-row">
            <div class="form-group" style="flex:1"><label>系统级</label><select v-model.number="editForm.useSYSType" class="form-control"><option :value="0">否</option><option :value="1">是</option></select></div>
            <div class="form-group" style="flex:1"><label>状态</label><select v-model.number="editForm.active" class="form-control"><option :value="0">禁用</option><option :value="1">启用</option></select></div>
          </div>
        </div>
        <div class="modal-footer"><button @click="closeEditModal" class="btn btn-default">取消</button><button @click="saveEdit" class="btn btn-primary">保存</button></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import http from '../utils/request'

const searchText = ref('')
const currentPage = ref(1)
const pageSize = ref(20)
const jumpPage = ref(1)
const sortColumn = ref('keyOrder')
const sortOrder = ref('asc')
const showColumnSelector = ref(false)
const showEditModal = ref(false)
const editingGuid = ref(null)

// 模块/子模块下拉选择
const moduleOptions = ref([])
const subModuleOptions = ref([])
const showModuleDropdown = ref(false)
const showSubModuleDropdown = ref(false)
const filteredModuleOptions = ref([])
const filteredSubModuleOptions = ref([])

const loadModuleOptions = async () => {
  try {
    const res = await http.get('/system/modules-list')
    moduleOptions.value = res.Data || res.data || []
  } catch (e) { /* ignore */ }
}
const loadSubModuleOptions = async (fromModule) => {
  try {
    const res = await http.get('/system/submodules-list', { params: { fromModule: fromModule || undefined } })
    subModuleOptions.value = res.Data || res.data || []
  } catch (e) { /* ignore */ }
}

const allColumns = ref([
  { key: 'fromModule', label: '模块', sortable: true, visible: true, width: '100px' },
  { key: 'fromSubModule', label: '子模块', sortable: true, visible: true, width: '100px' },
  { key: 'description', label: '描述', sortable: true, visible: true, width: '140px' },
  { key: 'keys', label: 'Keys', sortable: true, visible: true, width: '120px' },
  { key: 'subKeys', label: 'SubKeys', sortable: true, visible: true, width: '100px' },
  { key: 'sValues', label: 'Values', sortable: false, visible: true, width: '120px' },
  { key: 'subValues', label: 'SubValues', sortable: false, visible: false, width: '100px' },
  { key: 'keyOrder', label: '排序', sortable: true, visible: false, width: '60px' },
  { key: 'createTime', label: '创建时间', sortable: true, visible: true, width: '140px' },
  { key: 'useSYSType', label: '系统级', sortable: true, visible: true, width: '70px' },
  { key: 'active', label: '状态', sortable: true, visible: true, width: '70px' }
])

const settings = ref([])
const editForm = reactive({
  fromModule: '', fromSubModule: '', description: '', fromPath: '', fromStyle: '',
  keys: '', subKeys: '', keyOrder: 0, higherKeys: '',
  sValues: '', subValues: '', useSYSType: 0, active: 1
})

const fmt = (v) => {
  if (v === undefined || v === null) return ''
  return String(v).replace('T', ' ').substring(0, 10)
}

const loadSettings = async () => {
  try {
    const res = await http.get('/system/settings', { params: { searchText: searchText.value || undefined } })
    const list = res.Data || res.data || []
    settings.value = list.map(s => ({
      guid: s.Guid || s.guid || '',
      fromModule: s.FromModule || s.fromModule || '',
      fromSubModule: s.FromSubModule || s.fromSubModule || '',
      description: s.Description || s.description || '',
      fromPath: s.FromPath || s.fromPath || '',
      fromStyle: s.FromStyle || s.fromStyle || '',
      keys: s.Keys || s.keys || '',
      subKeys: s.SubKeys || s.subKeys || '',
      keyOrder: s.KeyOrder ?? s.keyOrder ?? 0,
      higherKeys: s.HigherKeys || s.higherKeys || '',
      sValues: s.SValues || s.sValues || '',
      subValues: s.SubValues || s.subValues || '',
      useSYSType: s.UseSYSType ?? s.useSYSType ?? 0,
      active: s.Active ?? s.active ?? 0,
      createTime: fmt(s.CreateTime || s.createTime)
    }))
  } catch (e) {
    console.error('加载系统参数失败', e)
  }
}

onMounted(loadSettings)

const visibleColumns = computed(() => allColumns.value.filter(c => c.visible))

const filteredSettings = computed(() => {
  let result = [...settings.value]
  if (searchText.value) {
    const kw = searchText.value.toLowerCase()
    result = result.filter(s =>
      (s.keys && s.keys.toLowerCase().includes(kw)) ||
      (s.fromModule && s.fromModule.toLowerCase().includes(kw)) ||
      (s.description && s.description.toLowerCase().includes(kw))
    )
  }
  // 排序
  const colDef = allColumns.value.find(c => c.key === sortColumn.value)
  if (colDef && colDef.sortable) {
    const key = sortColumn.value
    result.sort((a, b) => {
      const va = a[key] ?? '', vb = b[key] ?? ''
      let cmp = 0
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb
      else cmp = String(va).localeCompare(String(vb))
      return sortOrder.value === 'desc' ? -cmp : cmp
    })
  }
  return result
})

const totalSettings = computed(() => filteredSettings.value.length)
const totalPages = computed(() => Math.ceil(totalSettings.value / pageSize.value) || 1)
const paginatedSettings = computed(() => {
  const s = (currentPage.value - 1) * pageSize.value
  return filteredSettings.value.slice(s, s + pageSize.value)
})

const visiblePages = computed(() => {
  const pages = [], total = totalPages.value, current = currentPage.value
  if (total <= 7) { for (let i = 1; i <= total; i++) pages.push(i) }
  else { pages.push(1); if (current > 4) pages.push('...'); for (let i = Math.max(2, current - 2); i <= Math.min(total - 1, current + 2); i++) pages.push(i); if (current < total - 3) pages.push('...'); pages.push(total) }
  return pages
})

const handleSort = (col) => {
  const colDef = allColumns.value.find(c => c.key === col)
  if (!colDef || !colDef.sortable) return
  if (sortColumn.value === col) sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  else { sortColumn.value = col; sortOrder.value = 'asc' }
}

const handleSearch = () => { currentPage.value = 1; loadSettings() }
const resetSearch = () => { searchText.value = ''; currentPage.value = 1; loadSettings() }
const prevPage = () => { if (currentPage.value > 1) currentPage.value-- }
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++ }
const goToPage = (p) => { if (typeof p === 'number' && p >= 1 && p <= totalPages.value) currentPage.value = p }
const handleJump = () => { const p = parseInt(jumpPage.value); p >= 1 && p <= totalPages.value ? currentPage.value = p : jumpPage.value = currentPage.value }

// 列设置
const toggleColumnSelector = () => { showColumnSelector.value = !showColumnSelector.value }
const updateVisibleColumns = () => {
  if (allColumns.value.filter(c => c.visible).length < 1) {
    const first = allColumns.value.find(c => !c.visible); if (first) first.visible = true
  }
}
const resetColumns = () => { allColumns.value.forEach(c => c.visible = true) }

// 列拖拽
const dragStart = (event, column) => { event.dataTransfer.setData('column', column.key) }
const dropColumn = (event, targetColumn) => {
  const draggedKey = event.dataTransfer.getData('column')
  if (draggedKey === targetColumn.key) return
  const draggedIndex = allColumns.value.findIndex(c => c.key === draggedKey)
  const targetIndex = allColumns.value.findIndex(c => c.key === targetColumn.key)
  if (draggedIndex !== -1 && targetIndex !== -1) {
    const [dragged] = allColumns.value.splice(draggedIndex, 1)
    allColumns.value.splice(targetIndex, 0, dragged)
  }
}

// 编辑
const handleAdd = () => {
  editingGuid.value = null
  Object.assign(editForm, {
    fromModule: '', fromSubModule: '', description: '', fromPath: '', fromStyle: '',
    keys: '', subKeys: '', keyOrder: 0, higherKeys: '',
    sValues: '', subValues: '', useSYSType: 0, active: 1
  })
  loadModuleOptions()
  loadSubModuleOptions()
  showEditModal.value = true
}

const handleEdit = (row) => {
  editingGuid.value = row.guid
  editForm.fromModule = row.fromModule
  editForm.fromSubModule = row.fromSubModule
  editForm.description = row.description
  editForm.fromPath = row.fromPath
  editForm.fromStyle = row.fromStyle
  editForm.keys = row.keys
  editForm.subKeys = row.subKeys
  editForm.keyOrder = row.keyOrder
  editForm.higherKeys = row.higherKeys
  editForm.sValues = row.sValues
  editForm.subValues = row.subValues
  editForm.useSYSType = row.useSYSType
  editForm.active = row.active
  loadModuleOptions()
  loadSubModuleOptions(row.fromModule)
  showEditModal.value = true
}

// 模块下拉
const onModuleFocus = () => {
  filteredModuleOptions.value = moduleOptions.value
  showModuleDropdown.value = true
}
const filterModuleOptions = () => {
  const kw = editForm.fromModule.toLowerCase()
  filteredModuleOptions.value = moduleOptions.value.filter(m => m.toLowerCase().includes(kw))
  showModuleDropdown.value = true
}
const selectModule = (m) => {
  editForm.fromModule = m
  showModuleDropdown.value = false
  loadSubModuleOptions(m)
}
const onModuleBlur = () => { setTimeout(() => { showModuleDropdown.value = false }, 200) }

// 子模块下拉
const onSubModuleFocus = () => {
  filteredSubModuleOptions.value = subModuleOptions.value
  showSubModuleDropdown.value = true
}
const filterSubModuleOptions = () => {
  const kw = editForm.fromSubModule.toLowerCase()
  filteredSubModuleOptions.value = subModuleOptions.value.filter(s => s.toLowerCase().includes(kw))
  showSubModuleDropdown.value = true
}
const selectSubModule = (s) => {
  editForm.fromSubModule = s
  showSubModuleDropdown.value = false
}
const onSubModuleBlur = () => { setTimeout(() => { showSubModuleDropdown.value = false }, 200) }

const closeEditModal = () => { showEditModal.value = false; editingGuid.value = null }

const saveEdit = async () => {
  const payload = {
    guid: editingGuid.value || '',
    fromModule: editForm.fromModule,
    fromSubModule: editForm.fromSubModule,
    description: editForm.description,
    fromPath: editForm.fromPath,
    fromStyle: editForm.fromStyle,
    keys: editForm.keys,
    subKeys: editForm.subKeys,
    keyOrder: editForm.keyOrder,
    higherKeys: editForm.higherKeys,
    sValues: editForm.sValues,
    subValues: editForm.subValues,
    useSYSType: editForm.useSYSType,
    active: editForm.active
  }
  try {
    if (editingGuid.value) {
      await http.put('/system/settings', payload)
      $message.success('更新成功')
    } else {
      await http.post('/system/settings', payload)
      $message.success('新增成功')
    }
    closeEditModal()
    loadSettings()
  } catch (e) {
    $message.error('保存失败: ' + (e.response?.data?.Msg || e.message))
  }
}

const handleDelete = async (row) => {
  if (!await $confirm(`确定删除 "${row.keys || row.fromModule}" 吗？`)) return
  try {
    await http.delete(`/system/settings/${row.guid}`)
    $message.success('删除成功')
    loadSettings()
  } catch (e) {
    $message.error('删除失败: ' + (e.response?.data?.Msg || e.message))
  }
}
</script>

<style scoped>
.view-container { padding: 20px; background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); display: flex; flex-direction: column; max-height: calc(100vh - 150px); overflow: hidden; }

.btn { padding: 6px 15px; border-radius: 4px; border: none; cursor: pointer; font-size: 13px; display: inline-flex; align-items: center; gap: 5px; transition: all 0.2s ease; }
.btn-primary { background-color: #1ab394; color: #fff; }
.btn-primary:hover { background-color: #18a689; }
.btn-default { background-color: #f3f3f4; color: #676a6c; border: 1px solid #e7eaec; }
.btn-default:hover { background-color: #e7eaec; }
.btn-danger { background-color: #d9534f; color: #fff; }
.btn-danger:hover { background-color: #c9302c; }
.btn-xs { padding: 3px 8px; font-size: 12px; margin-right: 3px; }
.btn-sm { padding: 5px 12px; font-size: 12px; }

.search-panel { background: #fff; border: 1px solid #e0e4e8; border-radius: 8px; padding: 20px; margin-bottom: 15px; flex-shrink: 0; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
.search-row { display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-end; }
.search-item { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
.search-item label { font-size: 13px; color: #6b7280; font-weight: 500; }
.search-item label i { margin-right: 2px; color: #1ab394; font-size: 12px; }
.search-input { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; width: 180px; outline: none; transition: border-color 0.2s, box-shadow 0.2s; background: #fafbfc; }
.search-input:focus { border-color: #1ab394; box-shadow: 0 0 0 3px rgba(26,179,148,0.12); background: #fff; }
.search-actions { display: flex; gap: 10px; margin-left: auto; align-self: flex-end; }
.search-btn { padding: 8px 18px; font-size: 13px; border-radius: 6px; font-weight: 500; }

.table-wrapper { border: 1px solid #e7eaec; border-radius: 4px; overflow: hidden; display: flex; flex-direction: column; flex: 1; min-height: 0; }
.table-header-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 15px; background-color: #f9f9f9; border-bottom: 1px solid #e7eaec; flex-shrink: 0; }
.table-container { overflow-y: auto; overflow-x: auto; flex: 1; min-height: 0; }
.data-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.data-table th, .data-table td { padding: 10px 15px; text-align: left; border-bottom: 1px solid #e7eaec; white-space: nowrap; font-size: 13px; color: #676a6c; overflow: hidden; text-overflow: ellipsis; }
.data-table th { background-color: #f5f5f5; font-weight: 600; position: sticky; top: 0; z-index: 2; }
.data-table tbody tr:hover { background: #f9fafb; }

.column-header { cursor: pointer; user-select: none; white-space: nowrap; }
.column-header:hover { background-color: #e7eaec; }
.column-header i { margin-left: 4px; color: #9ca3af; font-size: 11px; vertical-align: middle; }
.column-header[draggable="true"]:active { cursor: grabbing; opacity: 0.6; }
.sorted-asc, .sorted-desc { background-color: #e8f5f3; }

.status-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 12px; font-weight: 500; }
.status-badge.enabled { background: #dcfce7; color: #16a34a; }
.status-badge.disabled { background: #fee2e2; color: #dc2626; }

.action-column { white-space: nowrap; }

.empty-cell { text-align: center; padding: 40px; color: #999; }

.pagination-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 15px; background-color: #f9f9f9; border-top: 1px solid #e7eaec; flex-shrink: 0; }
.pagination-info { font-size: 13px; color: #676a6c; }
.pagination-controls { display: flex; align-items: center; gap: 10px; }
.page-numbers { display: flex; gap: 5px; }
.page-btn { width: 32px; height: 32px; border: 1px solid #e7eaec; background-color: #fff; color: #676a6c; border-radius: 4px; cursor: pointer; font-size: 13px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; }
.page-btn:hover:not(.disabled) { background-color: #f9f9f9; border-color: #1ab394; color: #1ab394; }
.page-btn.active { background-color: #1ab394; border-color: #1ab394; color: #fff; }
.page-btn.disabled { cursor: not-allowed; opacity: 0.5; }
.pagination-btn { width: 32px; height: 32px; padding: 0; display: flex; align-items: center; justify-content: center; }
.page-size-selector { display: flex; align-items: center; gap: 5px; margin-right: 10px; padding-right: 10px; border-right: 1px solid #e7eaec; font-size: 13px; }
.page-size-input { width: 60px; padding: 5px 8px; font-size: 13px; }
.page-jump { display: flex; align-items: center; gap: 5px; margin-left: 10px; padding-left: 10px; border-left: 1px solid #e7eaec; }
.page-jump span { font-size: 13px; color: #676a6c; }
.jump-input { width: 50px; padding: 5px 8px; border: 1px solid #e7eaec; border-radius: 4px; font-size: 13px; text-align: center; }
.jump-input:focus { outline: none; border-color: #1ab394; box-shadow: 0 0 0 2px rgba(26,179,148,0.2); }
.jump-btn { padding: 5px 12px; }

/* 列设置 */
.column-checkbox { display: flex; align-items: center; gap: 8px; padding: 6px 0; cursor: pointer; font-size: 13px; color: #676a6c; }
.column-settings-btn { padding: 4px 10px; font-size: 12px; }

/* 弹窗 */
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-container { background: #fff; border-radius: 8px; box-shadow: 0 8px 30px rgba(0,0,0,0.18); display: flex; flex-direction: column; max-height: 85vh; width: 460px; }
.modal-container.modal-lg { width: 700px; max-width: 90vw; }
.modal-container.modal-sm { width: 360px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e7eaec; flex-shrink: 0; }
.modal-header h3 { margin: 0; font-size: 16px; color: #3a4a63; }
.modal-header h4 { margin: 0; font-size: 14px; color: #1a1a1a; }
.close-btn { background: none; border: none; font-size: 18px; color: #999; cursor: pointer; padding: 4px 8px; border-radius: 4px; }
.close-btn:hover { background: #f0f0f0; color: #333; }
.modal-body { padding: 20px; overflow-y: auto; flex: 1; }
.modal-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; flex-shrink: 0; }

/* 表单 */
.form-group { margin-bottom: 14px; }
.form-group label { display: block; margin-bottom: 4px; font-size: 13px; font-weight: 500; color: #676a6c; }
.form-row { display: flex; gap: 14px; }
.form-control { width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 13px; box-sizing: border-box; }
.form-control:focus { outline: none; border-color: #1ab394; box-shadow: 0 0 0 2px rgba(26,179,148,0.12); }

/* 下拉选择框 */
.combo-box { position: relative; }
.combo-dropdown { position: absolute; top: 100%; left: 0; right: 0; z-index: 1050; background: #fff; border: 1px solid #d1d5db; border-radius: 0 0 6px 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); max-height: 180px; overflow-y: auto; }
.combo-option { padding: 7px 12px; font-size: 13px; cursor: pointer; color: #374151; }
.combo-option:hover { background: #ecfdf5; color: #059669; }
</style>
